import { useEffect, useMemo, useState } from "react";
import { portalApi, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Clock, CalendarPlus, Info, Loader2 } from "lucide-react";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const primerDia = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const ultimoDia = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const ESTILO = {
  clase: "bg-primary/15 text-primary",
  reposicion: "bg-emerald-500/15 text-emerald-600",
  hueco: "bg-amber-400/20 text-amber-700 dark:text-amber-400 border border-dashed border-amber-400/60",
};

export const PortalCalendario = ({ hijo }) => {
  const [mes, setMes] = useState(() => primerDia(new Date()));
  const [data, setData] = useState(null);
  const [reservando, setReservando] = useState(null);

  const desde = iso(primerDia(mes));
  const hasta = iso(ultimoDia(mes));

  const load = () =>
    portalApi
      .get(`/portal/alumnos/${hijo.id}/calendario?desde=${desde}&hasta=${hasta}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ eventos: [] }));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hijo.id, desde, hasta]);

  const porDia = useMemo(() => {
    const map = {};
    (data?.eventos || []).forEach((e) => {
      map[e.fecha] = map[e.fecha] || [];
      map[e.fecha].push(e);
    });
    return map;
  }, [data]);

  const celdas = useMemo(() => {
    const inicio = primerDia(mes);
    const offset = (inicio.getDay() + 6) % 7;
    const total = ultimoDia(mes).getDate();
    const out = Array.from({ length: offset }, () => null);
    for (let d = 1; d <= total; d += 1) out.push(new Date(mes.getFullYear(), mes.getMonth(), d));
    return out;
  }, [mes]);

  const reservar = async (e) => {
    setReservando(e.sesion_id);
    try {
      const { data } = await portalApi.post(`/portal/reposiciones/${e.repo_id}/agendar`, { sesion_id: e.sesion_id });
      toast.success(`Reposición reservada para el ${data.fecha} a las ${data.hora_inicio}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setReservando(null);
    }
  };

  const huecos = (data?.eventos || []).filter((e) => e.tipo === "hueco");

  return (
    <div data-testid="portal-calendario">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" className="rounded-lg" data-testid="portal-mes-anterior"
            onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() - 1, 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <p className="font-display text-lg font-bold capitalize min-w-[170px] text-center" data-testid="portal-mes-actual">
            {mes.toLocaleDateString("es-ES", { month: "long", year: "numeric" })}
          </p>
          <Button variant="outline" size="sm" className="rounded-lg" data-testid="portal-mes-siguiente"
            onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() + 1, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="hidden sm:flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-primary/40" /> Clase</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-emerald-500/50" /> Reposición</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-amber-400/60" /> Hueco libre</span>
        </div>
      </div>

      <Card className="rounded-2xl p-3 sm:p-5 border-border shadow-sm overflow-x-auto">
        <div className="min-w-[640px]">
          <div className="grid grid-cols-7 gap-2 mb-2">
            {DIAS.map((d) => (
              <p key={d} className="text-xs uppercase tracking-wider text-muted-foreground font-semibold text-center py-2">{d}</p>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {celdas.map((d, i) => {
              if (!d) return <div key={`v${i}`} />;
              const key = iso(d);
              const items = porDia[key] || [];
              const hoy = key === iso(new Date());
              return (
                <div key={key} data-testid={`portal-dia-${key}`}
                  className={`min-h-[92px] rounded-xl border p-2 ${hoy ? "border-primary bg-accent/40" : "border-border"}`}>
                  <span className={`text-xs font-bold ${hoy ? "text-primary" : "text-muted-foreground"}`}>{d.getDate()}</span>
                  <div className="space-y-1 mt-1.5">
                    {items.map((e, j) => (
                      <div key={`${e.sesion_id}-${j}`} data-testid={`portal-evento-${e.tipo}-${e.sesion_id}`}
                        className={`rounded-lg px-1.5 py-1 text-[11px] font-semibold leading-tight ${ESTILO[e.tipo]}`}>
                        {e.hora_inicio} {e.tipo === "hueco" ? "Hueco libre" : e.clase}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {data?.reposiciones_pendientes > 0 && (
        <div className="mt-10">
          <h3 className="font-display text-xl font-bold mb-2">Reserva tu reposición</h3>
          <p className="text-sm text-muted-foreground mb-5 flex items-center gap-2">
            <Info className="h-4 w-4" /> Solo se pueden reservar huecos con más de {data.antelacion_horas} h de antelación.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {huecos.map((e) => (
              <Card key={e.sesion_id} className="rounded-2xl p-5 border-border shadow-sm" data-testid={`portal-hueco-${e.sesion_id}`}>
                <p className="font-display font-bold">{e.clase}</p>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" /> {e.fecha} · {e.hora_inicio}–{e.hora_fin}
                </p>
                {e.profesor && <Badge variant="outline" className="rounded-full mt-3">{e.profesor}</Badge>}
                <Button className="rounded-full font-semibold w-full mt-5" disabled={reservando === e.sesion_id}
                  onClick={() => reservar(e)} data-testid={`portal-reservar-${e.sesion_id}`}>
                  {reservando === e.sesion_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CalendarPlus className="h-4 w-4 mr-2" /> Reservar</>}
                </Button>
              </Card>
            ))}
            {huecos.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="portal-sin-huecos-mes">
                No hay huecos disponibles este mes. Prueba el mes siguiente.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
